import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Table, toast } from '../../components/ui';
import { api, ApiClientError } from '../../services/api-client';
import { useAuthStore } from '@ipsoftsrl/frontend-auth';

interface UserData {
  profileId: string;
  firstName: string;
  lastName: string;
  codiceFiscale: string;
  personalEmail: string;
  dateOfBirth: string;
  cityOfBirth: string;
  gender: string;
  nationality: string;
  phoneNumber: string;
  mobileNumber: string;
  status: string;
  censedAt: string;
  registeredAt: string;
}

interface AuditLogEntry {
  log_id: string;
  timestamp: string;
  action: string;
  resource_type: string;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  success: boolean;
}

interface DocLogEntry {
  log_id: string;
  document_id: string;
  action: string;
  read_at: string;
  file_name: string;
  tag: string;
  tag_extra: string;
  area: string;
}

const STATUS_LABELS: Record<string, string> = {
  CENSED: '🟡 Censito',
  INVITED: '📧 Invitato',
  REGISTERED: '✅ Registrato',
  ACTIVE: '🟢 Attivo',
  SUSPENDED: '🔴 Sospeso',
  DEACTIVATED: '⚫ Disattivato',
};

const ACTION_LABELS: Record<string, string> = {
  USER_CENSED: 'Censimento utente',
  USER_CREATED: 'Creazione utente',
  USER_UPDATED: 'Aggiornamento utente',
  FIRST_ACCESS_OTP_SENT: 'OTP inviato',
  OTP_VERIFIED: 'OTP verificato',
  OTP_FAILED: 'OTP fallito',
  OTP_BLOCKED: 'OTP bloccato',
  USER_REGISTRATION_COMPLETED: 'Registrazione completata',
  PERMISSIONS_MODIFIED: 'Permessi modificati',
  ACCESS_REVOKED: 'Accesso revocato',
  VIEW: '👁️ Visualizzazione',
  DOWNLOAD: '📥 Download',
  ACKNOWLEDGE: '✔️ Presa visione',
};

type Tab = 'info' | 'audit' | 'documents';

/**
 * Admin page: User detail — view/edit user info + activity logs.
 */
export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editEmail, setEditEmail] = useState('');
  const [editingEmail, setEditingEmail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('info');

  const userGroups = useAuthStore((s) => s.user?.groups) || [];
  const isAdmin = userGroups.includes('Administrators');

  useEffect(() => {
    if (!id) return;
    api.get<UserData>(`/admin/users/${id}`)
      .then((res) => { setUser(res.data); setEditEmail(res.data.personalEmail); })
      .catch(() => toast.error('Errore nel caricamento utente'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSaveEmail = async () => {
    if (!id || !editEmail) return;
    setSaving(true);
    try {
      await api.put(`/admin/users/${id}`, { personalEmail: editEmail });
      setUser((prev) => prev ? { ...prev, personalEmail: editEmail } : prev);
      setEditingEmail(false);
      toast.success('Email aggiornata');
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Errore');
    } finally {
      setSaving(false);
    }
  };

  const handleResendEmail = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await api.put(`/admin/users/${id}`, { resendRegistrationEmail: true });
      toast.success('Email di registrazione reinviata');
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Errore');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Caricamento...</p>;
  if (!user) return <p>Utente non trovato</p>;

  return (
    <div className="user-detail-page">
      <div className="page-header">
        <h2>{user.firstName} {user.lastName}</h2>
        <span>{STATUS_LABELS[user.status] || user.status}</span>
      </div>

      {/* Tabs */}
      <div className="tabs" role="tablist" style={{ display: 'flex', gap: '0', borderBottom: '2px solid #e2e8f0', marginBottom: '1.5rem' }}>
        <button
          role="tab"
          aria-selected={activeTab === 'info'}
          onClick={() => setActiveTab('info')}
          style={{ padding: '0.75rem 1.5rem', border: 'none', background: 'none', cursor: 'pointer', borderBottom: activeTab === 'info' ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: '-2px', fontWeight: activeTab === 'info' ? 600 : 400 }}
        >
          Informazioni
        </button>
        {isAdmin && (
          <>
            <button
              role="tab"
              aria-selected={activeTab === 'audit'}
              onClick={() => setActiveTab('audit')}
              style={{ padding: '0.75rem 1.5rem', border: 'none', background: 'none', cursor: 'pointer', borderBottom: activeTab === 'audit' ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: '-2px', fontWeight: activeTab === 'audit' ? 600 : 400 }}
            >
              Attività
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'documents'}
              onClick={() => setActiveTab('documents')}
              style={{ padding: '0.75rem 1.5rem', border: 'none', background: 'none', cursor: 'pointer', borderBottom: activeTab === 'documents' ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: '-2px', fontWeight: activeTab === 'documents' ? 600 : 400 }}
            >
              Log Documenti
            </button>
          </>
        )}
      </div>

      {/* Tab content */}
      {activeTab === 'info' && (
        <>
          <section className="detail-section">
            <h3>Dati Anagrafici</h3>
            <div className="detail-grid">
              <div className="detail-field"><label>Nome</label><span>{user.firstName}</span></div>
              <div className="detail-field"><label>Cognome</label><span>{user.lastName}</span></div>
              <div className="detail-field"><label>Codice Fiscale</label><span>{user.codiceFiscale}</span></div>
              <div className="detail-field"><label>Data di nascita</label><span>{user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString('it-IT') : '-'}</span></div>
              <div className="detail-field"><label>Comune di nascita</label><span>{user.cityOfBirth || '-'}</span></div>
              <div className="detail-field"><label>Sesso</label><span>{user.gender === 'M' ? 'Maschio' : 'Femmina'}</span></div>
              <div className="detail-field"><label>Censito il</label><span>{user.censedAt ? new Date(user.censedAt).toLocaleDateString('it-IT') : '-'}</span></div>
              <div className="detail-field"><label>Registrato il</label><span>{user.registeredAt ? new Date(user.registeredAt).toLocaleDateString('it-IT') : '-'}</span></div>
            </div>
          </section>

          <section className="detail-section">
            <h3>Contatti</h3>
            <div className="detail-grid">
              <div className="detail-field" style={{ gridColumn: '1 / -1' }}>
                <label>Email</label>
                {editingEmail ? (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid #dee2e6' }} />
                    <Button size="sm" onClick={handleSaveEmail} loading={saving}>Salva</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingEmail(false); setEditEmail(user.personalEmail); }}>Annulla</Button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span>{user.personalEmail}</span>
                    <Button size="sm" variant="ghost" onClick={() => setEditingEmail(true)}>Modifica</Button>
                  </div>
                )}
              </div>
              <div className="detail-field"><label>Telefono</label><span>{user.phoneNumber || '-'}</span></div>
              <div className="detail-field"><label>Cellulare</label><span>{user.mobileNumber || '-'}</span></div>
            </div>
          </section>

          <section className="detail-section">
            <h3>Azioni</h3>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <Button variant="secondary" onClick={handleResendEmail} loading={saving}>Reinvia email di registrazione</Button>
            </div>
          </section>
        </>
      )}

      {activeTab === 'audit' && <AuditLogTab userId={id!} />}
      {activeTab === 'documents' && <DocumentLogTab userId={id!} />}
    </div>
  );
}

// ─── Audit Log Tab ──────────────────────────────────────────────────────────

function AuditLogTab({ userId }: { userId: string }) {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => { loadLogs(); }, [page]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get<{ data: AuditLogEntry[]; pagination: { totalPages: number } }>(`/admin/users/${userId}/audit?page=${page}&pageSize=20`);
      setLogs(res.data.data);
      setTotalPages(res.data.pagination.totalPages);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (logId: string) => {
    setExpandedId(prev => prev === logId ? null : logId);
  };

  if (loading) return <p>Caricamento...</p>;
  if (logs.length === 0) return <p style={{ color: '#718096' }}>Nessuna attività registrata.</p>;

  return (
    <div>
      <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '40px 160px 1fr 1fr 60px', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontSize: '0.8rem', fontWeight: 600, color: '#4a5568' }}>
          <span></span>
          <span>Data/Ora</span>
          <span>Azione</span>
          <span>Dettaglio</span>
          <span>Esito</span>
        </div>

        {/* Rows */}
        {logs.map((log) => (
          <div key={log.log_id}>
            <div
              onClick={() => toggleExpand(log.log_id)}
              style={{
                display: 'grid',
                gridTemplateColumns: '40px 160px 1fr 1fr 60px',
                padding: '0.75rem 1rem',
                borderBottom: '1px solid #e2e8f0',
                cursor: 'pointer',
                background: expandedId === log.log_id ? '#f0f9ff' : '#fff',
                transition: 'background 0.1s',
                fontSize: '0.875rem',
                alignItems: 'center',
              }}
              onMouseOver={(e) => { if (expandedId !== log.log_id) e.currentTarget.style.background = '#fafafa'; }}
              onMouseOut={(e) => { if (expandedId !== log.log_id) e.currentTarget.style.background = '#fff'; }}
            >
              <span style={{ color: '#718096' }}>{expandedId === log.log_id ? '▼' : '▶'}</span>
              <span>{new Date(log.timestamp).toLocaleString('it-IT')}</span>
              <span>{ACTION_LABELS[log.action] || log.action}</span>
              <span style={{ color: '#718096', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {(() => {
                  const d = (log.details || {}) as Record<string, unknown>;
                  const parts: string[] = [];
                  const dev = d.device as Record<string, string> | undefined;
                  const geo = d.geolocation as Record<string, string> | undefined;
                  if (dev?.browser) parts.push(dev.browser);
                  if (dev?.os) parts.push(dev.os);
                  if (geo?.city) parts.push(`📍 ${geo.city}, ${geo.country}`);
                  return parts.join(' • ') || '-';
                })()}
              </span>
              <span>{log.success ? '✅' : '❌'}</span>
            </div>

            {/* Expanded detail */}
            {expandedId === log.log_id && (
              <div style={{ padding: '1rem 1.5rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <AuditDetailView log={log} />
              </div>
            )}
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
          <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Precedente</Button>
          <span style={{ alignSelf: 'center', fontSize: '0.875rem' }}>Pagina {page} di {totalPages}</span>
          <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Successiva →</Button>
        </div>
      )}
    </div>
  );
}

function AuditDetailView({ log }: { log: AuditLogEntry }) {
  const d = (log.details || {}) as Record<string, unknown>;
  const device = d.device as Record<string, string> | undefined;
  const geo = d.geolocation as Record<string, string> | undefined;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.875rem' }}>
      <DetailField label="Azione" value={ACTION_LABELS[log.action] || log.action} />
      <DetailField label="Data/Ora" value={new Date(log.timestamp).toLocaleString('it-IT')} />
      <DetailField label="Esito" value={log.success ? 'Successo' : 'Fallito'} />
      <DetailField label="Risorsa" value={log.resource_type} />
      <DetailField label="IP Address" value={log.ip_address || '-'} />

      {device && (
        <>
          <DetailField label="Browser" value={device.browser || '-'} />
          <DetailField label="Sistema Operativo" value={device.os || '-'} />
          <DetailField label="Mobile" value={device.mobile === 'true' ? 'Sì' : 'No'} />
          {device.raw && <div style={{ gridColumn: '1 / -1' }}><DetailField label="User Agent" value={device.raw} /></div>}
        </>
      )}

      {geo && (
        <>
          <DetailField label="Città" value={geo.city || '-'} />
          <DetailField label="Regione" value={geo.region || '-'} />
          <DetailField label="Paese" value={geo.country || '-'} />
          <DetailField label="Provider" value={geo.org || '-'} />
          <DetailField label="Coordinate" value={geo.loc || '-'} />
        </>
      )}

      {!!d.email && <DetailField label="Email" value={String(d.email)} />}
      {!!d.name && <DetailField label="Nome" value={String(d.name)} />}
      {!!d.timezone && <DetailField label="Fuso orario" value={String(d.timezone)} />}
      {!!d.language && <DetailField label="Lingua" value={String(d.language)} />}
      {!!d.screenResolution && <DetailField label="Risoluzione schermo" value={String(d.screenResolution)} />}
      {!!d.clientId && <DetailField label="Client ID" value={String(d.clientId)} />}
      {!!d.triggerSource && <DetailField label="Tipo autenticazione" value={String(d.triggerSource)} />}
      {!!d.groups && <DetailField label="Gruppi" value={String(d.groups)} />}
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: '#718096', fontSize: '0.75rem', marginBottom: '0.15rem' }}>{label}</div>
      <div style={{ color: '#1a202c' }}>{value}</div>
    </div>
  );
}

// ─── Document Log Tab ───────────────────────────────────────────────────────

function DocumentLogTab({ userId }: { userId: string }) {
  const [logs, setLogs] = useState<DocLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => { loadLogs(); }, [page]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get<{ data: DocLogEntry[]; pagination: { totalPages: number } }>(`/admin/users/${userId}/document-logs?page=${page}&pageSize=20`);
      setLogs(res.data.data);
      setTotalPages(res.data.pagination.totalPages);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      key: 'read_at', header: 'Data/Ora', width: '160px',
      render: (item: DocLogEntry) => new Date(item.read_at).toLocaleString('it-IT'),
    },
    {
      key: 'action', header: 'Azione', width: '140px',
      render: (item: DocLogEntry) => ACTION_LABELS[item.action] || item.action,
    },
    {
      key: 'tag', header: 'Tipo documento',
      render: (item: DocLogEntry) => item.tag || '-',
    },
    {
      key: 'file_name', header: 'File',
      render: (item: DocLogEntry) => (
        <span title={item.file_name}>{item.file_name?.length > 40 ? item.file_name.substring(0, 37) + '...' : item.file_name}</span>
      ),
    },
    {
      key: 'area', header: 'Area', width: '100px',
      render: (item: DocLogEntry) => item.area || '-',
    },
  ];

  return (
    <div>
      <Table<DocLogEntry> columns={columns} data={logs} keyField="log_id" loading={loading} emptyMessage="Nessun accesso ai documenti registrato." />
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
          <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Precedente</Button>
          <span style={{ alignSelf: 'center', fontSize: '0.875rem' }}>Pagina {page} di {totalPages}</span>
          <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Successiva →</Button>
        </div>
      )}
    </div>
  );
}
